/**
 * The ☰ main menu (z8nrz7f7g1, z8nrz7f7g3): every page the user may open, A–Z
 * by its Home tile name, each with the tabs the user may open.
 *
 * Built from the Home tile registry (`dashboardNav`) and the shared tab gates
 * (`pageTabs`), never a list of its own, so the menu cannot disagree with Home
 * or with a page's tab bar. Pure, so the ordering and filtering are tested.
 */

import type { DashboardNavCard } from './dashboardNav'
import type { PageTab } from './pageTabs'

export type MainMenuTab = { id: string; label: string; href: string }

export type MainMenuEntry = {
  id: string
  label: string
  href: string
  /** Empty for a page without tabs. */
  tabs: MainMenuTab[]
}

type BuildOptions = {
  /** The Home tile gate: permission and feature flag. */
  isAllowed: (card: DashboardNavCard) => boolean
  /** The tabs of a route path the user may open. */
  tabsFor: (path: string) => PageTab[]
}

const byLabel = (a: { label: string }, b: { label: string }) =>
  a.label.localeCompare(b.label, 'en', { sensitivity: 'base' })

/** A menu href's route path and `?tab=`. */
export function splitHref(href: string): { path: string; tab: string | null } {
  const [path, query = ''] = href.split('?')
  return { path, tab: new URLSearchParams(query).get('tab') }
}

export function buildMainMenu(cards: DashboardNavCard[], { isAllowed, tabsFor }: BuildOptions): MainMenuEntry[] {
  const allowed = cards.filter(isAllowed)
  const tabsByPath = new Map<string, PageTab[]>()
  const tabsOf = (path: string) => {
    if (!tabsByPath.has(path)) tabsByPath.set(path, tabsFor(path))
    return tabsByPath.get(path)!
  }
  const pagePaths = new Set(
    allowed.map((card) => splitHref(card.href)).filter((h) => h.tab === null).map((h) => h.path),
  )

  return (
    allowed
      // A tile that opens a tab (Pending Inventory, Storage, Users) is already
      // listed under its page, so it is not listed twice. It stays on its own
      // when its page is not listed, or the page does not offer that tab.
      .filter((card) => {
        const { path, tab } = splitHref(card.href)
        return tab === null || !pagePaths.has(path) || !tabsOf(path).some((t) => t.value === tab)
      })
      .map((card) => {
        const { path, tab } = splitHref(card.href)
        const tabs =
          tab === null
            ? tabsOf(path)
                .map((t) => ({ id: `${card.id}:${t.value}`, label: t.label, href: `${path}?tab=${t.value}` }))
                .sort(byLabel)
            : []
        return { id: card.id, label: card.label, href: card.href, tabs }
      })
      .sort(byLabel)
  )
}

/**
 * Menu-only shortcuts: entries that open a page's tab but stay listed on their
 * own, even when the page and that tab are listed too. For a tab people look
 * for by its own name (Purchase Orders, under Configure). They are not Home
 * tiles, so Home's three even columns stay as they are. Sorted in with the rest.
 */
export function withMenuShortcuts(entries: MainMenuEntry[], shortcuts: MainMenuEntry[]): MainMenuEntry[] {
  const ids = new Set(entries.map((e) => e.id))
  return [...entries, ...shortcuts.filter((s) => !ids.has(s.id))].sort(byLabel)
}

export type FilteredMenuEntry = MainMenuEntry & {
  /** True when a tab matched, so the page opens to show it. */
  forceExpanded: boolean
}

/**
 * Case-insensitive substring match on page and tab names. A page that matches
 * keeps all its tabs. A page that matches only through tabs keeps just those,
 * and opens.
 */
export function filterMainMenu(entries: MainMenuEntry[], query: string): FilteredMenuEntry[] {
  const q = query.trim().toLowerCase()
  if (!q) return entries.map((entry) => ({ ...entry, forceExpanded: false }))
  const matches = (label: string) => label.toLowerCase().includes(q)

  return entries.flatMap((entry) => {
    const matchedTabs = entry.tabs.filter((tab) => matches(tab.label))
    if (matches(entry.label)) return [{ ...entry, forceExpanded: matchedTabs.length > 0 }]
    if (matchedTabs.length > 0) return [{ ...entry, tabs: matchedTabs, forceExpanded: true }]
    return []
  })
}

export type MainMenuRow =
  | {
      kind: 'page'
      id: string
      label: string
      href: string
      hasTabs: boolean
      expanded: boolean
      /** Held open by the filter, so it cannot be closed. */
      forced: boolean
    }
  | { kind: 'tab'; id: string; label: string; href: string; parentId: string }

/** The rows on screen, top to bottom: what the arrow keys walk. */
export function visibleMenuRows(entries: FilteredMenuEntry[], expandedIds: ReadonlySet<string>): MainMenuRow[] {
  return entries.flatMap((entry) => {
    const hasTabs = entry.tabs.length > 0
    const expanded = hasTabs && (entry.forceExpanded || expandedIds.has(entry.id))
    const page: MainMenuRow = {
      kind: 'page',
      id: entry.id,
      label: entry.label,
      href: entry.href,
      hasTabs,
      expanded,
      forced: hasTabs && entry.forceExpanded,
    }
    if (!expanded) return [page]
    return [page, ...entry.tabs.map((tab): MainMenuRow => ({ kind: 'tab', ...tab, parentId: entry.id }))]
  })
}
